import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { Fragment, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { useGetVirtualAccounts } from 'src/api/query/creditcards'
import { useOnboardingDropoffRedirect } from 'src/app/Home/useOnboardingDropoffRedirect'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import EnableVirtualCardsModal from 'src/component-modal/EnableVirtualCardsModal/EnableVirtualCardsModal'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import HomeActivityBox from 'src/components/HomeActivityBox/HomeActivityBox'
import HomepageChart from 'src/components/HomepageChart/HomepageChart'
import HomepageTopMetrics from 'src/components/HomepageTopMetrics/HomepageTopMetrics'
import HomePromotionsStack from 'src/components/HomePromotionsStack/HomePromotionsStack'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectsWithCountsBox from 'src/components/ObjectsWithCountsBox/ObjectsWithCountsBox'
import OrgAdminBox from 'src/components/OrgAdminBox/OrgAdminBox'
import TransactionsOverviewBox from 'src/components/TransactionsOverviewBox/TransactionsOverviewBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { ImageNames, OrganizationRoles, svgImageNames } from 'src/enums'
import CVSVerificationHomepageAlert from 'src/feature/CVSVerificationHomepageAlert/CVSVerificationHomepageAlert'
import EnableVirtualCardCTA from 'src/feature/EnableVirtualCardCTA/EnableVirtualCardCTA'
import EnableVirtualCardSuccessCTA from 'src/feature/EnableVirtualCardSuccessCTA/EnableVirtualCardSuccessCTA'
import PhysicalCardHomepageError from 'src/feature/PhysicalCardHomepageError/PhysicalCardHomepageError'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import {
  useColorOrGrayscaleImage,
  useColorOrGrayscaleSVG,
} from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { TransactionStatus } from 'src/models/transaction.models'
import { UserFeature } from 'src/models/user.models'
import {
  accountOverviewUrl,
  accountsAccountsUrl,
  budgetsUrl,
  createVirtualCardUrl,
  myWalletPhysicalCardsUrl,
  myWalletTransactionsURL,
  myWalletVirtualCardsUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryCreditCardsPaginated } from 'src/queries/creditCard.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { useQueryTransactions } from 'src/queries/transactions.queries'
import { getOrganizationCardApprovalsEnabled } from 'src/redux/actions/data/organizations.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectIsAuthenticatedFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectCardApprovalsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectCreditCardsActiveDelegateCountStatisticsFromState,
  selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveSourceCountStatisticsFromState,
  selectCreditCardsCountStatisticsFromState,
  selectCreditCardsDelegateCountStatisticsFromState,
  selectCreditCardsDelegateUserMeCountStatisticsFromState,
  selectCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFromState,
  selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCountStatisticsFromState,
  selectCreditCardsSourceCountStatisticsFromState,
  selectCreditCardsTimedOutSourceCountStatisticsFromState,
  selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { AccentImageUp } from 'src/style/keyframeUtils'
import { css } from 'styled-components'

const VIRTUAL_CARD_SUCCESS_CTA_DISMISSED_KEY = 'virtualCardSuccessCTADismissed'

const Home: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const {
    results: statisticsQueries,
    anyLoading: reactQueryStatisticsLoading,
  } = useQueryStatistics({
    statisticsKeys: [StatisticsKeys.VirtualCardsActiveCount],
  })
  const virtualCardsActiveCount =
    statisticsQueries?.VirtualCardsActiveCount?.data ?? 0
  const creditCardsSourceBudgetsEnabledCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCountStatisticsFromState,
  )
  const creditCardsPendingSourceUserMeCount = useSelector(
    selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
  )
  const virtualCardsRecipientMeCount = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const virtualCardsActiveOrPendingRecipientMeCount = useSelector(
    selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState,
  )
  const creditCardsActiveDelegateCount = useSelector(
    selectCreditCardsActiveDelegateCountStatisticsFromState,
  )
  const creditCardsActiveSourceCount = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const creditCardsTimedOutSourceCount = useSelector(
    selectCreditCardsTimedOutSourceCountStatisticsFromState,
  )
  const creditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount =
    useSelector(
      selectCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFromState,
    )
  const creditCardsDelegateCount = useSelector(
    selectCreditCardsDelegateCountStatisticsFromState,
  )
  const creditCardsSourceCount = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const creditCardsCount = useSelector(
    selectCreditCardsCountStatisticsFromState,
  )
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const appName = useSelector(selectAppName)
  const remoteConfigLoaded = useSelector(selectLoadedFromRemoteConfigState)
  const creditCardsActiveSourceCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsDelegateUserMeCount = useSelector(
    selectCreditCardsDelegateUserMeCountStatisticsFromState,
  )
  const user = useSelector(selectUserListModelFromState)

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const dispatch = useBackgroundDispatch()
  const history = useHistory()

  const { isResumingOnboarding } = useOnboardingDropoffRedirect()

  const [enableVirtualModalCardId, setEnableVirtualModalCardId] = useState<
    string | undefined
  >()
  const [successCTADismissed, setSuccessCTADismissed] = useState(
    () => !!localStorage.getItem(VIRTUAL_CARD_SUCCESS_CTA_DISMISSED_KEY),
  )
  const [justEnrolledVirtualCard, setJustEnrolledVirtualCard] = useState(false)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const topMetricsStatsLoaded =
    !reactQueryStatisticsLoading &&
    statisticsLoaded.creditCardsActiveDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded &&
    statisticsLoaded.creditCardsPendingSourceUserMeCountStatisticsLoaded &&
    statisticsLoaded.creditCardsCountStatisticsLoaded &&
    statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded

  const relatedToCreditCards = creditCardsCount > 0
  const hasVirtualCards = virtualCardsRecipientMeCount > 0

  const myOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)
  const hasOrgOwnerPermissions = user?.hasOrgOwnerPermissions
  const hasOrgAdminPermissions = user?.hasOrgAdminPermissions
  const isGuestUser =
    user?.organizationRole === OrganizationRoles.guest && showGuestRole

  const {
    ORGANIZATION_USER_STATISTICS: canViewOrganizationUserStatistics,
    REGISTER_ACCOUNT: canRegisterAccount,
  } =
    useQueryUserFeatures([
      UserFeature.ORGANIZATION_USER_STATISTICS,
      UserFeature.REGISTER_ACCOUNT,
    ]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const hasFullOrgBookkeeperPermissions =
    canViewOrganizationUserStatistics || user?.hasOrgAdminPermissions
  const homescreenTopMetricsAccent = useColorOrGrayscaleSVG(
    svgImageNames.HomescreenTopMetricsAccent,
  )
  const homescreenOrgAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenOrgAccent,
  )
  const homescreenAccountAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenAccountAccent,
  )
  const homescreenCardAccent = useColorOrGrayscaleImage(
    ImageNames.HomescreenCardAccent,
  )
  // CHECK FOR FAILED PHYSICAL CARDS
  const { data: pendingPhysicalCardsData } = useQueryCreditCardsPaginated({
    filters: {
      type: CreditCardType.Source,
      displayStatuses: CreditCardDisplayStatus.Pending,
      virtualCardsSupported: false,
      count: 2,
      user: 'me',
    },
  })

  const { data: cvsVerificationCardsData } = useQueryCreditCardsPaginated({
    filters: {
      type: CreditCardType.Source,
      displayStatuses: CreditCardDisplayStatus.Pending,
      needsCardholderVerification: true,
      count: 2,
      user: 'me',
    },
  })

  // transactions
  const {
    data: transactionsData,
    isFetching: transactionsLoading,
    complete: transactionsComplete,
  } = useQueryTransactions({
    filters: {
      sort: '-date',
      recipientOrPhysicalCardUserId: user ? [user.id] : undefined,
      status: [
        TransactionStatus.Cleared,
        TransactionStatus.Declined,
        TransactionStatus.Pending,
        TransactionStatus.AuthReversal,
      ],
      since: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      until: moment().format('YYYY-MM-DD'),
    },
    enabled:
      isAuthenticated &&
      (isGuestUser || (hasVirtualCards && !relatedToCreditCards)),
    perPage: 3,
  })

  // Fetch virtual accounts with NOT_STARTED status for enable virtual cards CTA
  const { data: virtualAccountsData } = useGetVirtualAccounts('NOT_STARTED', {
    enabled: isAuthenticated && hasOrgOwnerPermissions,
  })

  const pendingVirtualAccounts = virtualAccountsData?.virtualAccounts ?? []
  const hasPendingVirtualAccounts = pendingVirtualAccounts.length > 0

  const {
    data: activeVirtualAccountsData,
    refetch: refetchActiveVirtualAccountsData,
  } = useGetVirtualAccounts('ACTIVE', {
    enabled: isAuthenticated && hasOrgOwnerPermissions,
  })

  const activeVirtualAccounts = activeVirtualAccountsData?.virtualAccounts ?? []
  const hasActiveVirtualAccounts = activeVirtualAccounts.length > 0

  const showEnableVirtualCardCTA =
    hasOrgOwnerPermissions &&
    hasPendingVirtualAccounts &&
    !justEnrolledVirtualCard

  const isOrgOwnerOrCardManagerOrAdmin =
    hasOrgOwnerPermissions ||
    user?.organizationRole === OrganizationRoles.cardManager ||
    user?.organizationRole === OrganizationRoles.admin

  const showEnableVirtualCardSuccessCTA =
    virtualCardsActiveCount === 0 &&
    hasActiveVirtualAccounts &&
    !showEnableVirtualCardCTA &&
    !successCTADismissed &&
    isOrgOwnerOrCardManagerOrAdmin

  useEffect((): void => {
    const topMetricsKeys = [
      appStatisticsKeys.CreditCardsActiveDelegateCountStatistics,
      appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
      appStatisticsKeys.CreditCardsPendingSourceUserMeCountStatistics,
      appStatisticsKeys.CreditCardsCountStatistics,
      appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
    ]

    const nonTopMetricsKeys = [
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCountStatistics,
      appStatisticsKeys.VirtualCardsPendingRequestCardholderMeCountStatistics,
      appStatisticsKeys.VirtualCardsActiveOrPendingRecipientMeCountStatistics,
      appStatisticsKeys.CreditCardsTimedOutSourceCountStatistics,
      appStatisticsKeys.CreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsDelegateCountStatistics,
      appStatisticsKeys.CreditCardsSourceCountStatistics,
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ]

    if (remoteConfigLoaded) {
      dispatchStatistics(topMetricsKeys)

      if (!hasFullOrgBookkeeperPermissions) {
        dispatchStatistics(nonTopMetricsKeys)
      }
    }
  }, [dispatchStatistics, hasFullOrgBookkeeperPermissions, remoteConfigLoaded])

  useEffect(() => {
    if (user?.organizationId !== undefined && cardApprovalRules === undefined) {
      dispatch(getOrganizationCardApprovalsEnabled(user.organizationId))
    }
  }, [user?.organizationId, cardApprovalRules])

  const yourAccountsBudgets = [
    ...(creditCardsSourceCount > 0 && creditCardsActiveSourceCount > 0
      ? [
          {
            id: 'accounts',
            count: creditCardsActiveSourceCount,
            objectName: translate('Active'),
            title: translate('Accounts'),
            to: accountsAccountsUrl(),
            ariaLabelSource: 'Accounts',
          },
        ]
      : []),
    ...(creditCardsSourceCount > 0 && creditCardsActiveSourceCount === 0
      ? [
          {
            id: 'accounts',
            isRed: true,
            objectName:
              creditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount >
              0
                ? translate('Verification Required')
                : creditCardsPendingSourceUserMeCount > 0
                  ? translate('Action Required')
                  : creditCardsTimedOutSourceCount > 0
                    ? translate('Timed Out')
                    : translate('Blocked'),
            title: translate('Accounts'),
            to: accountsAccountsUrl(),
            ariaLabelSource: 'Accounts',
          },
        ]
      : []),
    ...(creditCardsDelegateCount > 0 || creditCardsSourceBudgetsEnabledCount > 0
      ? [
          {
            id: 'budgets',
            count: creditCardsActiveDelegateCount,
            objectName: translate('Active'),
            title: translate('Budgets'),
            to: budgetsUrl(),
            ariaLabelSource: 'Budgets',
          },
        ]
      : []),
    ...(creditCardsDelegateCount > 0 ||
    creditCardsSourceCount > 0 ||
    virtualCardsActiveCount > 0
      ? [
          {
            id: 'virtualCards',
            objectName: translate('Active'),
            count: virtualCardsActiveCount,
            title: translate('Virtual Cards'),
            to: virtualCardsUrl(),
            ariaLabelSource: 'Virtual Cards',
          },
        ]
      : []),
  ]

  const borderGutterSMUpValue = parseInt(
    styleConstants.heights.borderGutterSMUp.replace('px', ''),
  )
  const borderGutterXXMDownValue = parseInt(
    styleConstants.heights.borderGutterXXMDown.replace('px', ''),
  )

  // EX-39432 — while we're still figuring out whether to redirect the user
  // to /onboarding/card (or actively about to), don't render the homepage
  // chrome content underneath. Authed.tsx suppresses the sidebar/header for
  // the same window.
  if (isResumingOnboarding) {
    return <LoadingBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Home | ${appName}`}</title>
        <meta name="description" content={`Home page of ${appName}`} />
      </Helmet>
      <BackgroundPage>
        {remoteConfigLoaded && topMetricsStatsLoaded ? (
          <BackgroundPageContent
            css={css`
              padding: ${borderGutterSMUpValue}px
                ${styleConstants.widths.borderGutterLGUp};
              ${styleConstants.heights.borderGutterSMUp};

              ${getBreakpointOnly(breakpointSizes.MD)(css`
                padding: ${borderGutterSMUpValue}px
                  ${styleConstants.widths.borderGutterMDOnly}
                  ${styleConstants.heights.borderGutterSMUp};
              `)}

              ${getBreakpointOnly(breakpointSizes.SM)(css`
                padding: ${borderGutterSMUpValue}px
                  ${styleConstants.widths.borderGutterSMOnly}
                  ${styleConstants.heights.borderGutterSMUp};
              `)}

            ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: ${borderGutterXXMDownValue}px
                  ${styleConstants.widths.borderGutterXSMDown}
                  ${styleConstants.heights.borderGutterXXMDown};
              `)}
            `}
          >
            {(pendingPhysicalCardsData?.creditCards || []).length > 0 && (
              <PhysicalCardHomepageError
                linkTo={
                  (pendingPhysicalCardsData?.creditCards || []).length === 1
                    ? accountOverviewUrl(
                        pendingPhysicalCardsData!.creditCards[0].id,
                      )
                    : myWalletPhysicalCardsUrl()
                }
              />
            )}
            {(cvsVerificationCardsData?.creditCards || []).length > 0 && (
              <CVSVerificationHomepageAlert
                linkTo={
                  (cvsVerificationCardsData?.creditCards || []).length === 1
                    ? accountOverviewUrl(
                        cvsVerificationCardsData!.creditCards[0].id,
                      )
                    : myWalletPhysicalCardsUrl()
                }
              />
            )}
            <div
              data-testid="welcome-back-message"
              css={css`
                margin-top: 20px;
                padding-left: 40px;
                display: flex;
                flex-direction: column;

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  margin-bottom: 10px;
                  padding-left: 20px;
                `)}
              `}
            >
              {hasFullOrgBookkeeperPermissions ? (
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  {translate('Welcome back to ${orgName},', {
                    orgName: myOrganization?.name || '',
                  })}
                  <div
                    css={css`
                      margin-top: 4px;
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {user?.firstNameOrEmail}
                  </div>
                </div>
              ) : (
                <>
                  <span
                    css={css`
                      ${getFontCss(
                        40,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )}

                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(
                          24,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )}
                      `)}
                    `}
                  >
                    {!relatedToCreditCards && !hasVirtualCards
                      ? translate('Welcome,')
                      : (relatedToCreditCards || hasVirtualCards) &&
                        translate('Welcome back,')}
                  </span>
                  <span
                    className={'user-name'}
                    css={css`
                      ${getFontCss(
                        40,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}

                      ${getBreakpointDown(breakpointSizes.XSM)(css`
                        ${getFontCss(
                          24,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `)}
                    `}
                  >
                    {user?.firstNameOrEmail}
                  </span>
                </>
              )}
            </div>
            {isGuestUser ? (
              <HorizontalContentCollection
                css={css`
                  height: 100%;
                `}
                splitLeft
              >
                <ObjectsWithCountsBox
                  objects={[
                    {
                      id: 'yourCards',
                      objectName: translate('Active & Pending'),
                      count: virtualCardsActiveOrPendingRecipientMeCount,
                      title: translate('Your Cards'),
                      to: myWalletVirtualCardsUrl(),
                      ariaLabelSource: 'Your Cards',
                    },
                  ]}
                />
                <TransactionsOverviewBox
                  title={translate('Recent Transactions')}
                  transactions={transactionsData?.transactions ?? []}
                  transactionsLoading={transactionsLoading}
                  transactionsComplete={transactionsComplete}
                  navAway={{
                    label: translate('View Your Transactions'),
                    to: {
                      pathname: myWalletTransactionsURL(),
                      search: '',
                      hash: '',
                      state: {},
                    },
                    ariaLabelSource: 'View Your Transactions',
                  }}
                />
              </HorizontalContentCollection>
            ) : (
              <HorizontalContentCollection
                css={css`
                  height: 100%;
                `}
                leftWidthPercentage={66}
              >
                <VerticalContentCollection
                  css={css`
                    height: 100%;
                    position: relative;
                  `}
                >
                  {hasFullOrgBookkeeperPermissions ? (
                    <>
                      {hasVirtualCardFeatures && (
                        <HomepageTopMetrics
                          activeVirtualCards={virtualCardsActiveCount}
                          activeVirtualCardsLoaded={
                            !reactQueryStatisticsLoading
                          }
                          activeBudgets={creditCardsActiveDelegateCount}
                          activeBudgetsLoaded={
                            statisticsLoaded.creditCardsActiveDelegateCountStatisticsLoaded
                          }
                          activeAccounts={creditCardsActiveSourceCount}
                          activeAccountsLoaded={
                            statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded
                          }
                          actionRequired={
                            creditCardsPendingSourceUserMeCount > 0
                          }
                        />
                      )}
                      <HomepageChart
                        creditCardsCountLoaded={
                          statisticsLoaded.creditCardsCountStatisticsLoaded
                        }
                        relatedToCreditCards={relatedToCreditCards}
                      />
                    </>
                  ) : (
                    <>
                      {(hasFullOrgBookkeeperPermissions ||
                        creditCardsSourceCount > 0 ||
                        creditCardsDelegateCount > 0) && (
                        <VerticalContentCollection
                          css={css`
                            position: relative;
                            box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 8px 0px;
                            border-radius: 10px;
                          `}
                        >
                          {hasFullOrgBookkeeperPermissions && (
                            <OrgAdminBox
                              css={css`
                                box-shadow: none;
                              `}
                              orgName={myOrganization?.name}
                              subtitle={translate('Your Organization')}
                              orgBookkeeper={hasFullOrgBookkeeperPermissions}
                              topBox={
                                creditCardsSourceCount > 0 ||
                                creditCardsDelegateCount > 0
                              }
                            />
                          )}
                          {(creditCardsSourceCount > 0 ||
                            creditCardsDelegateCount > 0) && (
                            <ObjectsWithCountsBox
                              css={css`
                                box-shadow: none;
                              `}
                              objects={yourAccountsBudgets}
                              orgBookkeeper={hasFullOrgBookkeeperPermissions}
                              bottomBox={hasFullOrgBookkeeperPermissions}
                            />
                          )}
                        </VerticalContentCollection>
                      )}
                      {hasVirtualCards ? (
                        <Fragment>
                          <ObjectsWithCountsBox
                            objects={[
                              {
                                id: 'yourCards',
                                objectName: translate('Active & Pending'),
                                count:
                                  virtualCardsActiveOrPendingRecipientMeCount,
                                title: translate('Your Cards'),
                                to: myWalletVirtualCardsUrl(),
                                ariaLabelSource: 'Your Cards',
                              },
                            ]}
                          />
                          {!relatedToCreditCards && (
                            <TransactionsOverviewBox
                              title={translate('Recent Transactions')}
                              transactions={
                                transactionsData?.transactions ?? []
                              }
                              transactionsLoading={transactionsLoading}
                              transactionsComplete={transactionsComplete}
                              navAway={{
                                label: translate('View Your Transactions'),
                                to: {
                                  pathname: myWalletTransactionsURL(),
                                  search: '',
                                  hash: '',
                                  state: {},
                                },
                                ariaLabelSource: 'View Your Transactions',
                              }}
                            />
                          )}
                        </Fragment>
                      ) : (
                        !relatedToCreditCards &&
                        (hasVirtualCardFeatures ? (
                          <HomePromotionsStack
                            hasVirtualCards={hasVirtualCards}
                            relatedToCreditCards={relatedToCreditCards}
                            hasOrgOwnerPermissions={hasOrgOwnerPermissions}
                            hasOrgAdminPermissions={hasOrgAdminPermissions}
                            activeCreditCards={
                              creditCardsActiveSourceCount || 0
                            }
                            canRegisterAccount={canRegisterAccount}
                            hasVirtualCardFeatures={hasVirtualCardFeatures}
                          />
                        ) : (
                          <HomepageChart
                            creditCardsCountLoaded={
                              statisticsLoaded.creditCardsCountStatisticsLoaded
                            }
                            relatedToCreditCards={relatedToCreditCards}
                          />
                        ))
                      )}
                    </>
                  )}
                  {(relatedToCreditCards || hasVirtualCards) && (
                    <div
                      css={css`
                        position: absolute;
                        width: 150px;
                        height: 40px;
                        right: 10px;
                        z-index: -1;
                        top: -60px;
                        overflow: hidden;

                        ${AccentImageUp(0)}

                        ${!!hasFullOrgBookkeeperPermissions &&
                        css`
                          overflow: visible;
                          ${AccentImageUp(40)}
                        `}
                      `}
                    >
                      {hasFullOrgBookkeeperPermissions ? (
                        <div
                          css={css`
                            ${hasFullOrgBookkeeperPermissions &&
                            css`
                              position: absolute;
                              top: -60px;
                            `}
                          `}
                        >
                          {homescreenTopMetricsAccent}
                        </div>
                      ) : (
                        <img
                          css={css`
                            width: 150px;
                            ${!!hasFullOrgBookkeeperPermissions &&
                            css`
                              position: absolute;
                              top: -60px;
                            `}
                          `}
                          src={
                            hasFullOrgBookkeeperPermissions
                              ? homescreenOrgAccent
                              : relatedToCreditCards
                                ? homescreenAccountAccent
                                : homescreenCardAccent
                          }
                          alt={''}
                        />
                      )}
                    </div>
                  )}
                </VerticalContentCollection>
                <VerticalContentCollection>
                  {showEnableVirtualCardCTA && (
                    <EnableVirtualCardCTA
                      onEnableClick={() => {
                        analytics.track('enable virtual cards home promo')
                        if (pendingVirtualAccounts.length === 1) {
                          setEnableVirtualModalCardId(
                            pendingVirtualAccounts[0].creditCardId,
                          )
                        } else {
                          history.push(myWalletPhysicalCardsUrl())
                        }
                      }}
                    />
                  )}
                  {showEnableVirtualCardSuccessCTA && (
                    <EnableVirtualCardSuccessCTA
                      onCreateClick={() => {
                        analytics.track('create first card home promo')
                        history.push(createVirtualCardUrl())
                      }}
                      onDismiss={() => {
                        setSuccessCTADismissed(true)
                        localStorage.setItem(
                          VIRTUAL_CARD_SUCCESS_CTA_DISMISSED_KEY,
                          'true',
                        )
                      }}
                    />
                  )}
                  <HomeActivityBox
                    preReqStatsLoaded={topMetricsStatsLoaded}
                    activeBudgets={creditCardsDelegateUserMeCount}
                    activeAccounts={
                      creditCardsActiveSourceCanIssueVirtualCardsCount
                    }
                  />
                </VerticalContentCollection>
              </HorizontalContentCollection>
            )}
          </BackgroundPageContent>
        ) : (
          <LoadingBackgroundPage />
        )}
      </BackgroundPage>
      <EnableVirtualCardsModal
        isOpen={!!enableVirtualModalCardId}
        showCloseButton
        onClose={() => setEnableVirtualModalCardId(undefined)}
        creditCardId={enableVirtualModalCardId ?? ''}
        onEnableComplete={() => {
          setEnableVirtualModalCardId(undefined)
          setJustEnrolledVirtualCard(true)
          refetchActiveVirtualAccountsData()
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <p>{translate('Success! Virtual cards are now enabled.')}</p>
              ),
            }),
          )
        }}
      />
    </>
  )
}

export default Home
